// The propose_relationship behavior's pure core: the job, the prompt, the
// parse and the behavior's own switches. One copy for both hosts of the runner
// (SPEC-custom-agent-template.md §4: "one runner, two hosts"):
//   browser — src/lib/llmNexusBatchService.ts and src/lib/relationshipRunner.ts
//   server  — supabase/functions/ambient/lib/relationshipServer.ts
//
// Given a batch of items, a relationship agent proposes structural
// relationships between pairs — dependency, prerequisite, aggregation,
// breakdown, elaboration, package, related — for a person to accept (the edge
// is created) or dismiss. Duplicates and variants are the cluster behavior's
// job (clusterCore.ts) and are never proposed here.

import { buildInstructionStack, type AgentCard } from './instructionStackCore.ts';
import { fenceReferenceDocs } from './referenceDocFence.ts';
import { LANGUAGE_NAMES } from './estimateValueCore.ts';

export const RELATIONSHIP_BATCH_SIZE = 25;

// ── The job (canon doc 20) ────────────────────────────────────────────────────

/** The contract the model works to — the appendix of the instruction stack. */
export const RELATIONSHIP_SYSTEM_PROMPT =
  'You are the Nexus agent for a strategic prioritization system. ' +
  'Your job is to scan a set of work items and identify structural relationship candidates between pairs. ' +
  'Respond with valid JSON only — no markdown, no extra text.';

export const RELATIONSHIP_JOB = {
  capabilityId: 'nexus_batch',
  role: 'Find structural relationships between items — what depends on what, what is part of what — for a person to review.',
  goal:
    'Make the structure of the backlog visible before a session ranks it, so dependencies and parts ' +
    'are decided on purpose rather than discovered late.',
  outputContract:
    'Per pair found: exactly one relationship proposal — source, target, type, optional subtype, ' +
    'direction, rationale and confidence. Never a cluster or variant. Nothing for pairs without evidence.',
  skillStack: ['Structural reading', 'Relationship typing', 'Direction judgment', 'Rationale compression'],
  reads: [
    'the titles, descriptions and categories of the items in the batch',
    'reference documents an administrator added',
  ],
  proposes: ['relationship proposals between two items, with type, direction and rationale'],
  /** The platform bound on the model parameters; an administrator may lower them (§2 H). */
  modelDefaults: { temperature: 0.2, maxTokens: 2000 },
} as const;

export function relationshipAgentCard(): AgentCard {
  return {
    capabilityId: RELATIONSHIP_JOB.capabilityId,
    role: RELATIONSHIP_JOB.role,
    goal: RELATIONSHIP_JOB.goal,
    permissions: { read: [...RELATIONSHIP_JOB.reads], propose: [...RELATIONSHIP_JOB.proposes] },
    memory: 'turn',
    skillStack: [...RELATIONSHIP_JOB.skillStack],
    outputContract: RELATIONSHIP_JOB.outputContract,
  };
}

/**
 * The system prompt for a relationship run: the full instruction stack with
 * the workspace's text as the subordinate overlay — it specializes, it never
 * replaces the contract (§2 H). The interactive advisor passes its own
 * contract as `appendix`; the batch scan uses RELATIONSHIP_SYSTEM_PROMPT.
 */
export function buildRelationshipSystemPrompt(params: {
  overlay?: string | null;
  language?: string | null;
  appendix?: string;
}): { systemPrompt: string; l0Version: string; l1Version: string } {
  const contract = params.appendix ?? RELATIONSHIP_SYSTEM_PROMPT;
  const overlay = params.overlay?.trim();
  const effectiveOverlay = overlay && overlay !== contract ? overlay : null;
  const language = params.language && LANGUAGE_NAMES[params.language]
    ? `Write each rationale in ${LANGUAGE_NAMES[params.language]}. Keep ids, relationship types and JSON keys exactly as given.`
    : null;
  const { systemPrompt, meta } = buildInstructionStack({
    card: relationshipAgentCard(),
    capabilityAppendix: [contract, language].filter(Boolean).join('\n'),
    workspaceOverlay: effectiveOverlay,
  });
  return { systemPrompt, l0Version: meta.l0Version, l1Version: meta.l1Version };
}

// ── The behavior's own switches (agent_settings.behavior_config) ─────────────

export const RELATIONSHIP_TYPES = ['dependency', 'prerequisite', 'aggregation', 'breakdown', 'elaboration', 'package', 'related'] as const;
export type RelationshipTypeKey = (typeof RELATIONSHIP_TYPES)[number];

export interface RelationshipBehaviorConfig {
  /** The least confidence the agent must state before it proposes. */
  minConfidence: 'low' | 'medium' | 'high';
  /** The relationship types it may propose. */
  types: RelationshipTypeKey[];
}

export const DEFAULT_RELATIONSHIP_CONFIG: RelationshipBehaviorConfig = { minConfidence: 'low', types: [...RELATIONSHIP_TYPES] };

export function resolveRelationshipConfigFrom(stored: Record<string, unknown> | null | undefined): RelationshipBehaviorConfig {
  const c = stored ?? {};
  const types = Array.isArray(c.types)
    ? (c.types as unknown[]).filter((t): t is RelationshipTypeKey => (RELATIONSHIP_TYPES as readonly string[]).includes(String(t)))
    : [...RELATIONSHIP_TYPES];
  return {
    minConfidence: c.minConfidence === 'medium' || c.minConfidence === 'high' ? c.minConfidence : 'low',
    types: types.length ? types : [...RELATIONSHIP_TYPES],
  };
}

const RANK = { low: 0, medium: 1, high: 2 } as const;

/** Keep only the proposals the agent is set to make: its types, at or above its confidence floor. */
export function applyRelationshipConfig(results: RelationshipResult[], config: RelationshipBehaviorConfig): RelationshipResult[] {
  return results.filter((r) =>
    (config.types as readonly string[]).includes(r.relationship_type) && RANK[r.confidence] >= RANK[config.minConfidence]);
}

/** The same pair and type, either way round, is the same proposal (the idempotency key). */
export function relationshipKey(r: Pick<RelationshipResult, 'source_item_id' | 'target_item_id' | 'relationship_type'>): string {
  return `${r.relationship_type}:${[r.source_item_id, r.target_item_id].sort().join('|')}`;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RelationshipItemInput {
  id: string;
  title: string;
  description?: string | null;
  category?: string | null;
}

export interface RelationshipReferenceDoc {
  title: string;
  content: string;
  docType?: string;
}

export interface RelationshipResult {
  source_item_id: string;
  target_item_id: string;
  /** dependency, prerequisite, aggregation, breakdown, elaboration, package or related. */
  relationship_type: string;
  /** informational|soft|hard; solution_spectrum|solution_variant; prioritize|select|prioritize_and_select. */
  relationship_subtype?: string;
  directionality: 'directed' | 'undirected';
  /** Role of source_item_id in a directed relationship. */
  source_item_role?: 'source' | 'target';
  rationale: string;
  confidence: 'high' | 'medium' | 'low';
}

// ── Prompt builder ────────────────────────────────────────────────────────────

export function buildRelationshipPrompt(items: RelationshipItemInput[], referenceDocs: RelationshipReferenceDoc[] = []): string {
  const itemList = items
    .map(
      (item, i) =>
        `${i + 1}. ID: ${item.id}\n   Title: ${item.title}` +
        (item.category ? `\n   Category: ${item.category}` : '') +
        (item.description?.trim() ? `\n   Description: ${item.description.trim()}` : ''),
    )
    .join('\n\n');

  const fenced = fenceReferenceDocs(referenceDocs, 'how work relates here');
  const refSection = fenced ? `\n\n${fenced}` : '';

  return `You are scanning the following ${items.length} work items for structural relationship candidates.

## Items
${itemList}${refSection}

## Relationship types to detect

**Dependency** (directed): Item A must happen before or enables Item B. Use subtypes:
- "informational": A informs B but B is not blocked
- "soft": A should complete before B but B can proceed carefully
- "hard": B is blocked until A completes

**Aggregation** (directed): Lower-level items together constitute a higher-level whole.
source_item_role of the constituent item is "source", the whole item is "target".

**Breakdown** (directed): A higher-level item decomposes into constituent lower-level parts.
source_item_role of the parent is "source", child parts are "target".

**Elaboration** (directed): Child items map a parent need's possibility continuum, typically choosing one from minimally sufficient through sufficient to ideal. Use subtypes:
- "solution_spectrum": possibilities at different sufficiency, ambition, altitude, or investment levels
- "solution_variant": different approaches at a similar sufficiency level
Unlike Competing, Elaboration requires a parent need and does not impose a peer-level at-most-one policy.

**Package** (undirected): Items that must move together as a unit (atomicity). Use subtypes:
- "prioritize": must be ranked together
- "select": must be selected/deselected together
- "prioritize_and_select": both constraints apply

**Related** (undirected): Items share a meaningful thematic or contextual connection but no structural rule applies.

**Do NOT propose cluster or variant relationships** — those are handled separately by the Twins agent.

## Task
Identify all meaningful structural relationship candidates you can find between pairs of items.
Only propose relationships where you have reasonable evidence from the item content.
Do not fabricate relationships — if in doubt, omit or use low confidence.

## Output format
Return a JSON array. Each element represents one proposed relationship:
[
  {
    "source_item_id": "<id of first item>",
    "target_item_id": "<id of second item>",
    "relationship_type": "<dependency|prerequisite|aggregation|breakdown|elaboration|package|related>",
    "relationship_subtype": "<subtype or null>",
    "directionality": "<directed|undirected>",
    "source_item_role": "<source|target|null>",
    "rationale": "<1-2 sentence explanation>",
    "confidence": "<high|medium|low>"
  }
]

Return an empty array [] if no structural relationships are evident.
Return JSON only — no markdown fences, no commentary.`;
}

// ── Result parser ─────────────────────────────────────────────────────────────

/**
 * Parse the model's reply. Pairs that are not both in the batch, pairs of an
 * item with itself, and clusters or variants are dropped. Null when the reply
 * is not a JSON array at all — a failed call, not "no relationships".
 */
export function parseRelationshipResponse(raw: string, validIds?: Set<string>): RelationshipResult[] | null {
  try {
    const match = raw.match(/\[[\s\S]*\]/);
    if (!match) return null;
    const arr = JSON.parse(match[0]) as unknown[];
    if (!Array.isArray(arr)) return null;

    return arr.reduce<RelationshipResult[]>((acc, item) => {
      const obj = item as Record<string, unknown>;
      if (
        !obj ||
        typeof obj.source_item_id !== 'string' ||
        typeof obj.target_item_id !== 'string' ||
        typeof obj.relationship_type !== 'string' ||
        typeof obj.rationale !== 'string'
      ) {
        return acc;
      }
      if (obj.relationship_type === 'cluster' || obj.relationship_type === 'variant') return acc;
      if (obj.source_item_id === obj.target_item_id) return acc;
      if (validIds && (!validIds.has(obj.source_item_id) || !validIds.has(obj.target_item_id))) return acc;

      const confidence = obj.confidence === 'high' || obj.confidence === 'low' ? obj.confidence : 'medium';
      acc.push({
        source_item_id: obj.source_item_id,
        target_item_id: obj.target_item_id,
        relationship_type: obj.relationship_type,
        relationship_subtype: typeof obj.relationship_subtype === 'string' && obj.relationship_subtype ? obj.relationship_subtype : undefined,
        directionality: obj.directionality === 'directed' ? 'directed' : 'undirected',
        source_item_role: obj.source_item_role === 'source' || obj.source_item_role === 'target' ? obj.source_item_role : undefined,
        rationale: obj.rationale,
        confidence,
      });
      return acc;
    }, []);
  } catch {
    return null;
  }
}
